import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { baseUrl } from "../config/baseUrl";
import axios from "axios";
import toastify from "../config/toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faQuestionCircle,
  faSave,
  faGlobe,
  faBook,
  faCalculator,
  faTimes,
  faFileText,
} from "@fortawesome/free-solid-svg-icons";
import JoditEditor from "jodit-react";
import { toast } from "react-toastify";
import "katex/dist/katex.min.css";
import editorConfig from "../utils/editorConfig";
import { hasLatexContent, renderLatexInHtml } from "../utils/latexPreview";

export default function AddQuestion() {
  const [formval, setFormval] = useState({
    // English fields
    question_english: "",
    option_a_english: "",
    option_b_english: "",
    option_c_english: "",
    option_d_english: "",
    option_e_english: "",
    explanation_english: "",
    passage_english: "",

    // Hindi fields
    question_hindi: "",
    option_A_hindi: "",
    option_B_hindi: "",
    option_C_hindi: "",
    option_D_hindi: "",
    option_E_hindi: "",
    explanation_hindi: "",
    passage_hindi: "",

    // Bengali fields
    question_bengali: "",
    option_1_bengali: "",
    option_2_bengali: "",
    option_3_bengali: "",
    option_4_bengali: "",
    option_e_bengali: "",
    explanation_bengali: "",
    passage_bengali: "",

    correct_answer: "",
    subject: "",
    topic: "",
    status: "",
    question_type: "standard", // standard, math, or passage
  });

  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [activeLanguage, setActiveLanguage] = useState("en");
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [mathExpression, setMathExpression] = useState("");
  const [showMathEditor, setShowMathEditor] = useState(false);
  const [activeField, setActiveField] = useState(null);

  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const richTextEditorConfig = useMemo(
    () => ({
      ...editorConfig,
      height: 320,
      placeholder: "Start typing here...",
    }),
    [],
  );

  // Fetch subjects and topics when component mounts
  useEffect(() => {
    const fetchData = async () => {
      setFetchingData(true);
      try {
        // Fetch subjects
        const subjectsResponse = await axios.get(`${baseUrl}/subject/list`, {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        });

        if (subjectsResponse.data && subjectsResponse.data.data) {
          setSubjects(subjectsResponse.data.data);
        }

        // Fetch topics
        const topicsResponse = await axios.get(`${baseUrl}/topic/list`, {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        });

        if (topicsResponse.data && topicsResponse.data.data) {
          setTopics(topicsResponse.data.data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toastify.error("Failed to load subjects or topics");
      } finally {
        setFetchingData(false);
      }
    };

    fetchData();
  }, []);

  // Load KaTeX for math rendering
  useEffect(() => {
    const katexCSS = document.createElement("link");
    katexCSS.rel = "stylesheet";
    katexCSS.href =
      "https://cdn.jsdelivr.net/npm/katex@0.16.8/dist/katex.min.css";
    document.head.appendChild(katexCSS);

    const katexScript = document.createElement("script");
    katexScript.src =
      "https://cdn.jsdelivr.net/npm/katex@0.16.8/dist/katex.min.js";
    document.head.appendChild(katexScript);

    return () => {
      document.head.removeChild(katexCSS);
      document.head.removeChild(katexScript);
    };
  }, []);

  const changeHandler = (e) => {
    const { name, value } = e.target;
    setFormval((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "subject" && { topic: "" }),
    }));
  };

  const handleEditorChange = (content, fieldName) => {
    setFormval((prev) => ({
      ...prev,
      [fieldName]: content,
    }));
  };

  // Math Expression Handler
  const insertMathExpression = (expression) => {
    if (activeField && window.katex) {
      try {
        // Validate the expression
        window.katex.renderToString(expression, {
          throwOnError: true,
          displayMode: false,
        });

        // Insert into active field
        const currentValue = formval[activeField] || "";
        const newValue = currentValue + ` $$${expression}$$ `;

        setFormval((prev) => ({
          ...prev,
          [activeField]: newValue,
        }));

        setMathExpression("");
        setShowMathEditor(false);
        // toastify.success("Math expression inserted successfully")
      } catch (error) {
        toastify.error("Invalid math expression");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Form validation
    if (!formval.question_english) {
      setLoading(false);
      return toastify.error("English question is required");
    }
    if (
      !formval.option_a_english ||
      !formval.option_b_english ||
      !formval.option_c_english ||
      !formval.option_d_english
    ) {
      setLoading(false);
      return toastify.error("All English options (A-D) are required");
    }
    if (!formval.correct_answer) {
      setLoading(false);
      return toastify.error("Correct answer is required");
    }
    if (!formval.subject) {
      setLoading(false);
      return toastify.error("Subject is required");
    }
    if (!formval.topic) {
      setLoading(false);
      return toastify.error("Topic is required");
    }
    if (!formval.status) {
      setLoading(false);
      return toastify.error("Status is required");
    }

    // Additional validation for passage type
    if (formval.question_type === "passage" && !formval.passage_english) {
      setLoading(false);
      return toastify.error(
        "English passage is required for passage-based questions"
      );
    }

    try {
      const response = await axios(`${baseUrl}/question`, {
        method: "POST",
        headers: {
          Authorization: localStorage.getItem("token"),
          "Content-Type": "application/json",
        },
        data: formval,
      });

      if (!response.data?.message === "Question added successfully") {
        toastify.error("An error occurred while saving the question");
        return;
      }

      toastify.success("Question added successfully");
      navigate("/questions");
    } catch (error) {
      console.error("Error:", error);
      toastify.error("Failed to add question");
    } finally {
      setLoading(false);
    }
  };

  const languages = [
    { code: "en", name: "English", flag: "🇺🇸" },
    { code: "hi", name: "हिंदी", flag: "🇮🇳" },
    { code: "bn", name: "বাংলা", flag: "🇧🇩" },
  ];

  const getFieldNames = (lang) => {
    switch (lang) {
      case "en":
        return {
          question: "question_english",
          optionA: "option_a_english",
          optionB: "option_b_english",
          optionC: "option_c_english",
          optionD: "option_d_english",
          optionE: "option_e_english",
          explanation: "explanation_english",
          passage: "passage_english",
        };
      case "hi":
        return {
          question: "question_hindi",
          optionA: "option_A_hindi",
          optionB: "option_B_hindi",
          optionC: "option_C_hindi",
          optionD: "option_D_hindi",
          optionE: "option_E_hindi",
          explanation: "explanation_hindi",
          passage: "passage_hindi",
        };
      case "bn":
        return {
          question: "question_bengali",
          optionA: "option_1_bengali",
          optionB: "option_2_bengali",
          optionC: "option_3_bengali",
          optionD: "option_4_bengali",
          optionE: "option_e_bengali",
          explanation: "explanation_bengali",
          passage: "passage_bengali",
        };
      default:
        return {};
    }
  };

  const filteredTopics = formval.subject
    ? topics.filter((topic) => topic?.subject?._id === formval.subject)
    : topics;

  const getLanguageKey = (lang) => {
    switch (lang) {
      case "en":
        return "english";
      case "hi":
        return "hindi";
      case "bn":
        return "bengali";
      default:
        return "english";
    }
  };

  return (
    <>
      <section className="main-sec">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="dashboard-title">
                <h4 className="dash-head">
                  <FontAwesomeIcon icon={faQuestionCircle} className="me-2" />
                  Add Question
                </h4>
              </div>
              <div className="custom-bredcump">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/">Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="/questions">Questions</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Add Question
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
            <div className="col-lg-6 d-flex justify-content-end">
              <Link to="/questions" className="btn btn-info text-white">
                <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
                Back
              </Link>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-12">
              <div className="cards edit-usr" style={{ position: "relative" }}>
                {fetchingData && (
                  <div className="loading-overlay">
                    <div className="loading-spinner"></div>
                    <span className="loading-text">
                      Loading subjects and topics...
                    </span>
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  {/* Question Type Selection */}
                  <div className="question-type-tabs">
                    <div
                      className={`question-type-tab ${formval.question_type === "standard" ? "active" : ""
                        }`}
                      onClick={() =>
                        setFormval((prev) => ({
                          ...prev,
                          question_type: "standard",
                        }))
                      }
                    >
                      <FontAwesomeIcon icon={faQuestionCircle} />
                      <span>Standard Question</span>
                    </div>
                    <div
                      className={`question-type-tab ${formval.question_type === "math" ? "active" : ""
                        }`}
                      onClick={() =>
                        setFormval((prev) => ({
                          ...prev,
                          question_type: "math",
                        }))
                      }
                    >
                      <FontAwesomeIcon icon={faCalculator} />
                      <span>Math Question</span>
                    </div>
                    <div
                      className={`question-type-tab ${formval.question_type === "passage" ? "active" : ""
                        }`}
                      onClick={() =>
                        setFormval((prev) => ({
                          ...prev,
                          question_type: "passage",
                        }))
                      }
                    >
                      <FontAwesomeIcon icon={faFileText} />
                      <span>Passage Question</span>
                    </div>
                  </div>

                  {/* Metadata Section */}
                  <div className="metadata-section">
                    <div className="metadata-title">
                      <FontAwesomeIcon icon={faBook} />
                      Question Metadata
                    </div>
                    <div className="row">
                      <div className="col-lg-4 mb-3">
                        <label htmlFor="subject" className="form-label">
                          Subject<sup className="text-danger">*</sup>
                        </label>
                        <select
                          className="form-control"
                          name="subject"
                          onChange={changeHandler}
                          value={formval?.subject}
                          disabled={fetchingData}
                        >
                          <option value="">Select Subject</option>
                          {subjects.map((subject) => (
                            <option key={subject._id} value={subject._id}>
                              {subject.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-lg-4 mb-3">
                        <label htmlFor="topic" className="form-label">
                          Topic<sup className="text-danger">*</sup>
                        </label>
                        <select
                          className="form-control"
                          name="topic"
                          onChange={changeHandler}
                          value={formval?.topic}
                          disabled={fetchingData || !formval.subject}
                        >
                          <option value="">Select Topic</option>
                          {filteredTopics.map((topic) => (
                            <option key={topic._id} value={topic._id}>
                              {topic.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-lg-4 mb-3">
                        <label htmlFor="status" className="form-label">
                          Status<sup className="text-danger">*</sup>
                        </label>
                        <select
                          className="form-control"
                          name="status"
                          onChange={changeHandler}
                          value={formval?.status}
                        >
                          <option value="">Select Status</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Passage Section - Only show for passage type */}
                  {formval.question_type === "passage" && (
                    <div className="passage-section">
                      <div className="passage-title">
                        <FontAwesomeIcon icon={faFileText} />
                        Reading Passage
                      </div>

                      {/* Language Tabs for Passage */}
                      <div className="language-tabs">
                        {languages.map((lang) => (
                          <div
                            key={lang.code}
                            className={`language-tab ${activeLanguage === lang.code ? "active" : ""
                              }`}
                            onClick={() => setActiveLanguage(lang.code)}
                          >
                            <span>{lang.flag}</span>
                            <span>{lang.name}</span>
                            <FontAwesomeIcon icon={faGlobe} />
                          </div>
                        ))}
                      </div>

                      {/* Passage Content for each language */}
                      {languages.map((lang) => {
                        const fields = getFieldNames(lang.code);
                        return (
                          <div
                            key={lang.code}
                            className={`language-content ${activeLanguage === lang.code ? "active" : ""
                              }`}
                          >
                            <div className="mb-4">
                              <label className="form-label">
                                Passage ({lang.name})
                                {lang.code === "en" && (
                                  <sup className="text-danger">*</sup>
                                )}
                              </label>
                              <div className="quill-container">
                                <JoditEditor
                                  value={formval[fields.passage] || ""}
                                  config={richTextEditorConfig}
                                  tabIndex={1}
                                  onBlur={() => {}}
                                  onChange={(content) =>
                                    handleEditorChange(content, fields.passage)
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Language Tabs */}
                  <div className="language-tabs">
                    {languages.map((lang) => (
                      <div
                        key={lang.code}
                        className={`language-tab ${activeLanguage === lang.code ? "active" : ""
                          }`}
                        onClick={() => setActiveLanguage(lang.code)}
                      >
                        <span>{lang.flag}</span>
                        <span>{lang.name}</span>
                        <FontAwesomeIcon icon={faGlobe} />
                      </div>
                    ))}
                  </div>

                  {/* Language Content */}
                  {languages.map((lang) => {
                    const fields = getFieldNames(lang.code);
                    return (
                      <div
                        key={lang.code}
                        className={`language-content ${activeLanguage === lang.code ? "active" : ""
                          }`}
                      >
                        <div className="row">
                          <div className="col-lg-12 mb-4">
                            <label
                              htmlFor={fields.question}
                              className="form-label"
                            >
                              Question ({lang.name})
                              {lang.code === "en" && (
                                <sup className="text-danger">*</sup>
                              )}
                            </label>
                            {formval.question_type === "math" && (
                              <div className="math-toolbar">
                                <button
                                  type="button"
                                  className="math-btn"
                                  onClick={() => {
                                    setActiveField(fields.question);
                                    setShowMathEditor(true);
                                  }}
                                >
                                  <FontAwesomeIcon
                                    icon={faCalculator}
                                    className="me-1"
                                  />
                                  Add Math
                                </button>
                              </div>
                            )}
                            <div className="quill-container">
                              <JoditEditor
                                value={formval[fields.question] || ""}
                                config={richTextEditorConfig}
                                tabIndex={1}
                                onBlur={() => {}}
                                onChange={(content) =>
                                  handleEditorChange(content, fields.question)
                                }
                              />
                            </div>
                            {hasLatexContent(formval[fields.question]) && (
                                <div className="mt-2">
                                  <span>Preview </span>
                                  <div
                                    className="latex-preview form-control mt-2"
                                    dangerouslySetInnerHTML={{
                                      __html: renderLatexInHtml(
                                        formval[fields.question]
                                      ),
                                    }}
                                  />
                                </div>
                              )}
                          </div>

                          <div className="col-lg-6 mb-4">
                            <div className="option-container">
                              <span className="option-label">
                                Option A ({lang.name})
                              </span>
                              {formval.question_type === "math" && (
                                <div className="math-toolbar">
                                  <button
                                    type="button"
                                    className="math-btn"
                                    onClick={() => {
                                      setActiveField(fields.optionA);
                                      setShowMathEditor(true);
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faCalculator} />
                                  </button>
                                </div>
                              )}
                              <input
                                type="text"
                                className="form-control"
                                name={fields.optionA}
                                placeholder={`Enter option A in ${lang.name}`}
                                onChange={changeHandler}
                                value={formval[fields.optionA]}
                              />
                              {hasLatexContent(formval[fields.optionA]) && (
                                  <div className="mt-2">
                                    <span>Preview </span>
                                    <div
                                      className="latex-preview form-control mt-2"
                                      dangerouslySetInnerHTML={{
                                        __html: renderLatexInHtml(
                                          formval[fields.optionA]
                                        ),
                                      }}
                                    />
                                  </div>
                                )}
                            </div>
                          </div>

                          <div className="col-lg-6 mb-4">
                            <div className="option-container">
                              <span className="option-label">
                                Option B ({lang.name})
                              </span>
                              {formval.question_type === "math" && (
                                <div className="math-toolbar">
                                  <button
                                    type="button"
                                    className="math-btn"
                                    onClick={() => {
                                      setActiveField(fields.optionB);
                                      setShowMathEditor(true);
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faCalculator} />
                                  </button>
                                </div>
                              )}
                              <input
                                type="text"
                                className="form-control"
                                name={fields.optionB}
                                placeholder={`Enter option B in ${lang.name}`}
                                onChange={changeHandler}
                                value={formval[fields.optionB]}
                              />
                              {hasLatexContent(formval[fields.optionB]) && (
                                  <div className="mt-2">
                                    <span>Preview </span>
                                    <div
                                      className="latex-preview form-control mt-2"
                                      dangerouslySetInnerHTML={{
                                        __html: renderLatexInHtml(
                                          formval[fields.optionB]
                                        ),
                                      }}
                                    />
                                  </div>
                                )}
                            </div>
                          </div>

                          <div className="col-lg-6 mb-4">
                            <div className="option-container">
                              <span className="option-label">
                                Option C ({lang.name})
                              </span>
                              {formval.question_type === "math" && (
                                <div className="math-toolbar">
                                  <button
                                    type="button"
                                    className="math-btn"
                                    onClick={() => {
                                      setActiveField(fields.optionC);
                                      setShowMathEditor(true);
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faCalculator} />
                                  </button>
                                </div>
                              )}
                              <input
                                type="text"
                                className="form-control"
                                name={fields.optionC}
                                placeholder={`Enter option C in ${lang.name}`}
                                onChange={changeHandler}
                                value={formval[fields.optionC]}
                              />
                              {hasLatexContent(formval[fields.optionC]) && (
                                  <div className="mt-2">
                                    <span>Preview </span>
                                    <div
                                      className="latex-preview form-control mt-2"
                                      dangerouslySetInnerHTML={{
                                        __html: renderLatexInHtml(
                                          formval[fields.optionC]
                                        ),
                                      }}
                                    />
                                  </div>
                                )}
                            </div>
                          </div>

                          <div className="col-lg-6 mb-4">
                            <div className="option-container">
                              <span className="option-label">
                                Option D ({lang.name})
                              </span>
                              {formval.question_type === "math" && (
                                <div className="math-toolbar">
                                  <button
                                    type="button"
                                    className="math-btn"
                                    onClick={() => {
                                      setActiveField(fields.optionD);
                                      setShowMathEditor(true);
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faCalculator} />
                                  </button>
                                </div>
                              )}
                              <input
                                type="text"
                                className="form-control"
                                name={fields.optionD}
                                placeholder={`Enter option D in ${lang.name}`}
                                onChange={changeHandler}
                                value={formval[fields.optionD]}
                              />
                              {hasLatexContent(formval[fields.optionD]) && (
                                  <div className="mt-2">
                                    <span>Preview </span>
                                    <div
                                      className="latex-preview form-control mt-2"
                                      dangerouslySetInnerHTML={{
                                        __html: renderLatexInHtml(
                                          formval[fields.optionD]
                                        ),
                                      }}
                                    />
                                  </div>
                                )}
                            </div>
                          </div>

                          <div className="col-lg-12 mb-4">
                            <div className="option-container">
                              <span className="option-label">
                                Option E ({lang.name}) - Optional
                              </span>
                              {formval.question_type === "math" && (
                                <div className="math-toolbar">
                                  <button
                                    type="button"
                                    className="math-btn"
                                    onClick={() => {
                                      setActiveField(fields.optionE);
                                      setShowMathEditor(true);
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faCalculator} />
                                  </button>
                                </div>
                              )}
                              <input
                                type="text"
                                className="form-control"
                                name={fields.optionE}
                                placeholder={`Enter option E in ${lang.name} (optional)`}
                                onChange={changeHandler}
                                value={formval[fields.optionE]}
                              />

                              {hasLatexContent(formval[fields.optionE]) && (
                                  <div className="mt-2">
                                    <span>Preview </span>
                                    <div
                                      className="latex-preview form-control"
                                      dangerouslySetInnerHTML={{
                                        __html: renderLatexInHtml(
                                          formval[fields.optionE]
                                        ),
                                      }}
                                    />
                                  </div>
                                )}
                            </div>
                          </div>

                          <div className="col-lg-12 mb-4">
                            <label
                              htmlFor={fields.explanation}
                              className="form-label"
                            >
                              Explanation ({lang.name})
                            </label>
                            {formval.question_type === "math" && (
                              <div className="math-toolbar">
                                <button
                                  type="button"
                                  className="math-btn"
                                  onClick={() => {
                                    setActiveField(fields.explanation);
                                    setShowMathEditor(true);
                                  }}
                                >
                                  <FontAwesomeIcon
                                    icon={faCalculator}
                                    className="me-1"
                                  />
                                  Add Math
                                </button>
                              </div>
                            )}

                            <div className="quill-container">
                              <JoditEditor
                                value={formval[fields.explanation] || ""}
                                config={richTextEditorConfig}
                                tabIndex={1}
                                onBlur={() => {}}
                                onChange={(content) =>
                                  handleEditorChange(content, fields.explanation)
                                }
                              />
                            </div>
                            {hasLatexContent(formval[fields.explanation]) && (
                                <div
                                  className="latex-preview form-control mt-2"
                                  dangerouslySetInnerHTML={{
                                    __html: renderLatexInHtml(
                                      formval[fields.explanation]
                                    ),
                                  }}
                                />
                              )}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Correct Answer Section */}
                  <div className="correct-answer-container">
                    <div className="correct-answer-title">
                      Select Correct Answer<sup className="text-danger">*</sup>
                    </div>
                    <div className="radio-container">
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="correct_answer"
                          value="A"
                          onChange={changeHandler}
                          checked={formval.correct_answer === "A"}
                        />
                        Option A
                      </label>
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="correct_answer"
                          value="B"
                          onChange={changeHandler}
                          checked={formval.correct_answer === "B"}
                        />
                        Option B
                      </label>
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="correct_answer"
                          value="C"
                          onChange={changeHandler}
                          checked={formval.correct_answer === "C"}
                        />
                        Option C
                      </label>
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="correct_answer"
                          value="D"
                          onChange={changeHandler}
                          checked={formval.correct_answer === "D"}
                        />
                        Option D
                      </label>
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="correct_answer"
                          value="E"
                          onChange={changeHandler}
                          checked={formval.correct_answer === "E"}
                        />
                        Option E
                      </label>
                    </div>
                  </div>

                  <div className="col-lg-12 text-center mt-4">
                    <button
                      type="submit"
                      className="btn btn-for-add text-white"
                      disabled={loading || fetchingData}
                    >
                      {loading && <div className="loading-spinner"></div>}
                      <FontAwesomeIcon icon={faSave} className="me-2" />
                      {loading ? "Saving..." : "Save Question"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Math Editor Modal */}
        {showMathEditor && (
          <div className="math-editor-modal">
            <div className="math-editor-content">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>Math Expression Editor</h5>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setShowMathEditor(false)}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              <div className="mb-3">
                <label className="form-label">LaTeX Expression</label>
                <input
                  type="text"
                  className="form-control"
                  value={mathExpression}
                  onChange={(e) => setMathExpression(e.target.value)}
                  placeholder="Enter LaTeX expression (e.g., x^2 + y^2 = z^2)"
                />
                {/* <small className="text-muted">
                                    Use LaTeX syntax. Example: x^2 for x², \frac{a}
                                    {b} for fractions, \sqrt{x} for square root
                                </small> */}
              </div>

              <div className="math-symbols">
                {[
                  { symbol: "\\alpha", display: "α" },
                  { symbol: "\\beta", display: "β" },
                  { symbol: "\\gamma", display: "γ" },
                  { symbol: "\\delta", display: "δ" },
                  { symbol: "\\pi", display: "π" },
                  { symbol: "\\theta", display: "θ" },
                  { symbol: "\\lambda", display: "λ" },
                  { symbol: "\\mu", display: "μ" },
                  { symbol: "\\sum", display: "Σ" },
                  { symbol: "\\int", display: "∫" },
                  { symbol: "\\frac{a}{b}", display: "a/b" },
                  { symbol: "\\sqrt{x}", display: "√x" },
                  { symbol: "x^2", display: "x²" },
                  { symbol: "x_1", display: "x₁" },
                  { symbol: "\\sin", display: "sin" },
                  { symbol: "\\cos", display: "cos" },
                  { symbol: "\\tan", display: "tan" },
                  { symbol: "\\log", display: "log" },
                  { symbol: "\\ln", display: "ln" },
                  { symbol: "\\infty", display: "∞" },
                  { symbol: "\\pm", display: "±" },
                  { symbol: "\\times", display: "×" },
                  { symbol: "\\div", display: "÷" },
                  { symbol: "\\leq", display: "≤" },
                  { symbol: "\\geq", display: "≥" },
                  { symbol: "\\neq", display: "≠" },
                  { symbol: "\\approx", display: "≈" },
                  { symbol: "\\rightarrow", display: "→" },
                ].map((item) => (
                  <div
                    key={item.symbol}
                    className="math-symbol"
                    onClick={() =>
                      setMathExpression((prev) => prev + item.symbol)
                    }
                    title={item.symbol}
                  >
                    {item.display}
                  </div>
                ))}
              </div>

              <div className="d-flex gap-2 justify-content-end">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowMathEditor(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => insertMathExpression(mathExpression)}
                  disabled={!mathExpression.trim()}
                >
                  Insert Expression
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </>
  );
}
